// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useEffect } from 'react'
import { Heart, ExternalLink } from 'lucide-react'
import { InfoButton } from '@/components/ui/InfoButton'
import { InfoBlock }  from '@/components/ui/InfoBlock'
import { YesNoStep, THREE_OPTIONS } from './YesNoStep'
import { useWizardStore } from '@/store'
import type { SaveStatus } from '@/hooks/useAutosave'

export function NewStep08Donor({ onSaveStatus }: { onSaveStatus: (s: SaveStatus) => void }) {
  const { answers, setAnswer } = useWizardStore()
  const value = answers.donorStatus ?? ''
  useEffect(() => { onSaveStatus('idle') }, [onSaveStatus])

  function handleChange(v: string) {
    setAnswer('donorStatus', v as 'yes' | 'no' | 'unknown')
    onSaveStatus('saved')
    setTimeout(() => onSaveStatus('idle'), 1500)
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start gap-3 rounded-xl bg-status-infoBg border border-sage-200 px-4 py-3">
        <span className="text-sm flex-shrink-0">ℹ️</span>
        <p className="text-xs text-status-infoText leading-relaxed">
          WilsApp registreert uw donorvoorkeur als persoonlijke leidraad. Dit vervangt uw officiële registratie in het Donorregister niet.
          Uw wettelijke donorkeuze staat in het <strong>landelijke Donorregister</strong>.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <InfoButton title="Donorregistratie"
          content={<>
            <p>In het Donorregister staat uw keuze over orgaandonatie. Geeft u zelf geen keuze door, dan legt de overheid volgens de wet een registratie 'geen bezwaar tegen orgaandonatie' vast. Controleer daarom altijd uw actuele keuze.</p>
            <p><strong>Controleren:</strong> kijk op donorregister.nl of uw wens correct is vastgelegd.</p>
            <p>WilsApp kan uw registratie niet inzien of wijzigen. U doet dit zelf met DigiD op donorregister.nl.</p>
          </> } />
      </div>
      <InfoBlock title="Goed om te weten - donorregistratie" variant="blue" defaultOpen={false}>
        <p>Uw donorkeuze wordt officieel vastgelegd in het landelijk Donorregister. Wat u hier invult is bedoeld zodat uw naasten uw keuze kennen en daar in het ziekenhuis niet over hoeven te twijfelen.</p>
        <p>Sinds de huidige wet staat iedereen vanaf achttien jaar in het register. Heeft u zelf nooit een keuze gemaakt, dan staat er een registratie 'geen bezwaar tegen orgaandonatie'. Dat verrast mensen geregeld; controleer en wijzig dit zelf met uw DigiD via mijn.donorregister.nl. U kunt ook aangeven welke organen u wel en niet wilt doneren.</p>
                        <p className="mt-2">
          <a href="https://www.donorregister.nl/" target="_blank" rel="noopener noreferrer"
             className="text-xs text-sage-600 underline underline-offset-2 hover:text-sage-800">
            Donorregister.nl - bekijk of wijzig uw officiële registratie →
          </a>
        </p>
        <p className="text-xs text-ink-4 mt-2 pt-2 border-t border-sand-200 leading-relaxed">
          WilsApp legt uw wensen vast als persoonlijke leidraad. Voor officiële registratie,
          juridische geldigheid of medische besluitvorming verwijzen wij naar de bevoegde instantie.
        </p>
      </InfoBlock>
      <YesNoStep
        title="Donorregistratie"
        question="Heeft u uw donorwens geregistreerd bij de overheid?"
        description="Het Donorregister is een officieel register van de Nederlandse overheid."
        value={value}
        onChange={handleChange}
        options={THREE_OPTIONS}
      />

      {/* Informatietekst - altijd zichtbaar */}
      <div className="bg-white rounded-2xl border border-sand-300 p-5 flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <Heart className="h-5 w-5 text-sage-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-ink mb-1.5">Over orgaandonatie</p>
            <p className="text-xs text-ink-3 leading-relaxed">
              Donorregistratie wordt officieel vastgelegd via de Nederlandse overheid.
              WilsApp kan deze registratie niet inzien of wijzigen, maar helpt u eraan denken dit zelf bij de overheid te controleren.
            </p>
          </div>
        </div>
        <button
          onClick={() => window.open('https://www.donorregister.nl', '_blank')}
          className="flex items-center justify-center gap-2 rounded-xl border border-sage-200 bg-sage-50 px-4 py-3 text-sm font-medium text-sage-700 hover:bg-sage-100 transition-colors"
        >
          <ExternalLink className="h-4 w-4" />
          Donorregistratie controleren (demo)
        </button>
      </div>
    </div>
  )
}
